import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Mail, Lock, KeyRound, ArrowLeft } from "lucide-react";
import { Logo, Alert } from "../components/ui";
import { useAuth } from "../context/AuthContext";
import { formatApiError } from "../lib/api";

// Kisi titik dengan gelombang diagonal: delay = jarak diagonal dari pojok
// kanan atas, sehingga pulse menular sampai kiri bawah lalu mengulang.
const WAVE_COLS = 9;
const WAVE_ROWS = 12;
function DotsWave() {
  const dots = [];
  for (let r = 0; r < WAVE_ROWS; r++) {
    for (let c = 0; c < WAVE_COLS; c++) {
      const dist = (WAVE_COLS - 1 - c) + r;
      dots.push(
        <span
          key={`${r}-${c}`}
          className="dot-wave block h-1.5 w-1.5 rounded-full bg-white"
          style={{ animationDelay: `${(dist * 0.14).toFixed(2)}s` }}
        />
      );
    }
  }
  return (
    <div
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 overflow-hidden"
    >
      <div
        className="absolute -right-8 top-0 grid h-full w-2/3"
        style={{
          gridTemplateColumns: `repeat(${WAVE_COLS}, minmax(0, 1fr))`,
          gridTemplateRows: `repeat(${WAVE_ROWS}, minmax(0, 1fr))`,
          placeItems: "center",
          maskImage: "linear-gradient(to bottom left, black 20%, transparent 85%)",
          WebkitMaskImage: "linear-gradient(to bottom left, black 20%, transparent 85%)",
        }}
      >
        {dots}
      </div>
    </div>
  );
}

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [totp, setTotp] = useState("");
  const [showTotp, setShowTotp] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const user = await login({ email, password, totp: totp || undefined });
      navigate(user.role === "ADMIN" ? "/admin" : user.role === "WORKER" ? "/worker" : "/customer");
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail === "mfa_required") {
        setShowTotp(true);
        setError("Akun admin memerlukan kode autentikasi (TOTP). Masukkan kode 6 digit dari aplikasi authenticator Anda.");
      } else if (detail === "mfa_setup_required") {
        setError("Akun admin ini belum mengaktifkan MFA. Hubungi Super Admin.");
      } else {
        setError(formatApiError(detail) || err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between bg-brand-700 text-white p-12 relative overflow-hidden">
        <div className="absolute -right-20 top-20 h-80 w-80 rounded-full bg-brand-500/40 blur-3xl" />
        <DotsWave />
        <div className="relative">
          <Logo light={true} />
        </div>
        <div className="relative">
          <h1 className="leading-tight">Selamat datang kembali.</h1>
          <p className="mt-4 text-lg text-brand-100 max-w-md">
            Masuk untuk mengelola pesanan, memantau progres, dan menikmati pembayaran yang aman.
          </p>
        </div>
        <p className="relative text-brand-200 text-sm">Pembayaran aman • Pekerja terverifikasi • Perlindungan pelanggan</p>
      </div>

      <div className="flex flex-col justify-center p-6 md:p-12 bg-[#f6f8fc]">
        <div className="w-full max-w-md mx-auto">
          <Link to="/" className="inline-flex items-center gap-2 text-brand-500 font-semibold mb-6" data-testid="back-home">
            <ArrowLeft className="h-4 w-4" /> Kembali
          </Link>
          <div className="lg:hidden mb-6"><Logo /></div>
          <h2 className="text-brand-800">Masuk ke akun Anda</h2>
          <p className="text-brand-400 mt-1">Belum punya akun?{" "}
            <Link to="/register" className="text-brand-600 font-semibold" data-testid="link-register">Daftar gratis</Link>
          </p>

          <form onSubmit={submit} className="mt-8 space-y-5" data-testid="login-form">
            {error && <Alert testid="login-error">{error}</Alert>}
            <div>
              <label className="field-label">Email</label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-brand-300" />
                <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                  className="field-input pl-12" placeholder="nama@email.com" data-testid="login-email" />
              </div>
            </div>
            <div>
              <label className="field-label">Kata sandi</label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-brand-300" />
                <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                  className="field-input pl-12" placeholder="Kata sandi Anda" data-testid="login-password" />
              </div>
            </div>
            {showTotp && (
              <div className="animate-fade-up">
                <label className="field-label">Kode Autentikasi (Admin)</label>
                <div className="relative">
                  <KeyRound className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-brand-300" />
                  <input inputMode="numeric" value={totp} onChange={(e) => setTotp(e.target.value)}
                    className="field-input pl-12 tracking-widest" placeholder="6 digit" data-testid="login-totp" />
                </div>
              </div>
            )}
            <button type="submit" disabled={loading} className="btn-primary w-full btn-lg" data-testid="login-submit">
              {loading ? "Memproses..." : "Masuk"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
